/*
 * VAT-aware selling rate helpers for the finance screens.
 *
 * The finance backend keeps `invoiceSalesAmount` as the VAT-EXCLUSIVE selling
 * rate (it is the revenue figure profit and margin are measured against) and
 * additionally exposes:
 *
 *   vatIncluded        true when the invoice was cut with VAT ("Taxable Sale")
 *   vatAmount          total VAT of the invoice(s)
 *   sellingRateExVat   same as invoiceSalesAmount
 *   sellingRateInclVat what the customer is actually billed (after returns)
 *
 * Screens should show `getBilledAmount` wherever they present the customer
 * billing / selling rate, and keep using the raw ex-VAT revenue for profit.
 * For invoices without VAT every helper falls back to the ex-VAT value, so
 * exempt invoices look exactly as before.
 */

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const getVatAmount = (row) =>
    Math.max(toNumber(row?.vatAmount ?? row?.vat_amount), 0);

export const hasVatIncluded = (row) =>
    getVatAmount(row) > 0 &&
    (row?.sellingRateInclVat ?? row?.selling_rate_incl_vat) !== undefined;

// `exVatAmount` is the page's own ex-VAT revenue figure (what it already
// shows today); it is returned untouched when the invoice carries no VAT.
export const getBilledAmount = (row, exVatAmount = 0) =>
    hasVatIncluded(row)
        ? toNumber(row?.sellingRateInclVat ?? row?.selling_rate_incl_vat)
        : toNumber(exVatAmount);

// Billed amount before issued returns are deducted (the "gross invoice" figure
// some screens show next to the net amount).
export const getGrossBilledAmount = (row, exVatGross = 0) =>
    hasVatIncluded(row)
        ? toNumber(
            row?.grossSellingRateInclVat ??
            row?.gross_selling_rate_incl_vat ??
            row?.sellingRateInclVat,
        )
        : toNumber(exVatGross);
